import {
    AutogenLayoutOfficePayrollProcessingPayeeDetail
} from "./autogen/AutogenLayoutOfficePayrollProcessingPayeeDetail";
import { FilterPanel, SecondaryFilterProps } from "@mcleod/dispatch";
import { HorizontalAlignment, ModelRow } from "@mcleod/core";
import { OfficePayeeData } from "@mcleod/settlement/src/PayeeData";
import {
    BlurEvent, Component, DataSourceMode, PrintableEvent, TableRow, TableRowBeforeSaveEvent, TableRowDisplayEvent,
    Textbox
} from "@mcleod/components";
import { TimecardProcessing } from "@mcleod/settlement/src/TimecardProcessing";
import { CommonTooltips } from "@mcleod/common";
import { getPayrollControlBoolean } from "@mcleod/general";

class OfficePayrollProcessingTaskFilter {
    static readonly PROCESS: SecondaryFilterProps = {
        id: "processStatus",
        caption: "Process",
        filter: (row: ModelRow) => {
            return row.get("ready_to_pay_flag") == "Y";
        }
    }
    static readonly HOLD: SecondaryFilterProps = {
        id: "holdStatus",
        caption: "Hold",
        filter: (row: ModelRow) => {
            return row.get("ready_to_pay_flag") == "N";
        }
    }
}

export class OfficePayrollProcessingPayeeDetail extends AutogenLayoutOfficePayrollProcessingPayeeDetail {
    public filterPanel: FilterPanel;

    onLoad(): void | Promise<void> {
        this.filterPanel = new FilterPanel([OfficePayrollProcessingTaskFilter.HOLD, OfficePayrollProcessingTaskFilter.PROCESS], this.tablePayAdjustments, this.panelTaskFilters);
        this.tablePayAdjustments.addButtonCaption = "Add Pay Adjustment";
        if (getPayrollControlBoolean("addl_tax_override_hours", false) === false) {
            this.tableTimecard.removeColumn("columnHeaderAdditionalTaxOverrideHours");
        }
    }

    public populateTimecardForPayee(payeeData: OfficePayeeData): void {
        if (!payeeData || !this.panelTimecardDetails.visible) {
            this.sourceTimecard.setRowsAndMode(DataSourceMode.UPDATE, []);
            return;
        }

        const timecardRow = payeeData.getTimecard() ?? this.sourceTimecard.data.find((row) => row.get("payee_id") === payeeData.getPayeeRow().get("payee_id"));
        if (timecardRow) {
            // if table already contains this payee's timecard, replace it
            // this avoids the flicker caused by hiding/redisplaying rows using setRowsAndMode
            const currentTimecardRow = this.sourceTimecard.data?.[0];
            if (currentTimecardRow?.get("id") === timecardRow.get("id")) {
                this.sourceTimecard.replaceRow(currentTimecardRow, timecardRow);
            }
            else {
                this.sourceTimecard.setRowsAndMode(DataSourceMode.UPDATE, [timecardRow]);
            }
        } else {
            this.sourceTimecard.setRowsAndMode(DataSourceMode.UPDATE, []);
        }
    }

    tableTimecardOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        tableRow.forEveryChildComponent((component) => {
            this.setHoursFieldsEnabled(component, modelRow);
            if (component instanceof Textbox && !modelRow.isNull("payee_process_status")) {
                component.enabled = false;
            }
            if (component instanceof Textbox) {
                component.addBlurListener((event: BlurEvent) => {
                    this.recalculateHoursAndTotals(tableRow);
                });
            }
        });
        const labelTotalPay = tableRow.findComponentById("labelTotalPay");
        if (labelTotalPay) {
            CommonTooltips.setTooltipContentLayout(labelTotalPay, modelRow.get("id"), "lme/backoffice/officepayroll/TimecardTotalsQuickInfo");
        }
    }

    private setHoursFieldsEnabled(component: Component, modelRow: ModelRow) {
        switch (component.id) {
            case "textboxHolidayUnits":
            case "textboxSickUnits":
            case "textboxRegularUnits": {
                component.enabled = modelRow.get("regular_rate", 0) > 0;
                component.tooltip = component.enabled ? null : "Missing rates";
                break;
            }
            case "textboxOvertimeUnits": {
                component.enabled = modelRow.get("overtime_rate", 0) > 0;
                component.tooltip = component.enabled ? null : "Missing rates";
                break;
            }
            case "textboxVacationUnits": {
                component.enabled = modelRow.get("vacation_rate", 0) > 0;
                component.tooltip = component.enabled ? null : "Missing rates";
            }
        }
    }

    private recalculateHoursAndTotals(tableRow: TableRow) {
        if (tableRow.validateSimple()) {
            const modelRow = tableRow.data as ModelRow;
            TimecardProcessing.recalculateHoursAndTotals(modelRow);
            tableRow.displayComponentValues();
        }
    }

    tableTimecardBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow = event.getTableRow();
        if (tableRow.validateSimple()) {
            this.recalculateHoursAndTotals(tableRow);
        }
    }

    tablePayAdjustmentsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        const textboxDeductCodeDescr = tableRow.findComponentById("textboxDeductCodeDescr") as Textbox;
        if (textboxDeductCodeDescr) {
            CommonTooltips.setTooltipContentLayout(textboxDeductCodeDescr, modelRow.get("deduct_code_id"), "lme/general/DeductCodeQuickInfo");
        }
    }

    textboxVacationOnPrintable(event: PrintableEvent): void {
       event.printableLabel.align = HorizontalAlignment.LEFT;
    }

    textboxSickOnPrintable(event: PrintableEvent): void {
        event.printableLabel.align = HorizontalAlignment.LEFT;
    }
}
